import React, { useEffect, useRef } from "react";
import styles from "./AppShell.module.css";
import { useUIStore } from "../../store/uiStore";
import AppSidebar from "./AppSidebar";
import { trackEvent } from "../../services/analyticsService";
import ChatSidebarView from "../chat/ChatSidebarView";
import DocumentsPage from "../pages/DocumentsPage";
import PromptsPage from "../pages/PromptsPage";
import MindspaceInfoPage from "../pages/MindspaceInfoPage";
import useAuthStore from "../../store/authStore";
import { useChatStore } from "../../store/chatStore";
import { useMindspaceStore } from "../../store/mindspaceStore";
import { initializeChatData } from "../../App";
import { logDebug, logError } from "../../utils/logging";

const AppShell: React.FC = () => {
  const route = useUIStore((s) => s.route);
  const { isSidebarOpenOnMobile, closeSidebarMobile, toggleSidebarMobile } = useUIStore();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    trackEvent("fullpage_pageview", { route });
  }, [route]);

  // Ensure initial auth data is loaded in fullpage mode (widget triggers this via ChatBubble)
  const isLoadingInitialData = useAuthStore((s) => s.isLoadingInitialData);
  const initialDataLoaded = useAuthStore((s) => !!s.jwt);
  const setLoadingInitialData = useAuthStore((s) => s.setLoadingInitialData);
  const setInitialData = useAuthStore((s) => s.setInitialData);
  const setInitialDataError = useAuthStore((s) => s.setInitialDataError);

  useEffect(() => {
    if (!initialDataLoaded && !isLoadingInitialData) {
      logDebug("AppShell: initializing auth data for fullpage mode...");
      initializeChatData(setLoadingInitialData, setInitialData, setInitialDataError).catch(
        (err) => logError("AppShell: failed to initialize auth data", err),
      );
    }
  }, [initialDataLoaded, isLoadingInitialData, setLoadingInitialData, setInitialData, setInitialDataError]);

  // Initialize mindspace (v2) when JWT becomes available
  const loadDefaultMindspace = useMindspaceStore((s) => s.loadDefaultMindspace);
  const isMindspaceLoading = useMindspaceStore((s) => s.isLoading);
  const isMindspaceInitialized = useMindspaceStore((s) => s.isInitialized);
  useEffect(() => {
    if (initialDataLoaded && !isMindspaceInitialized && !isMindspaceLoading) {
      logDebug("AppShell: JWT available, initializing default mindspace...");
      loadDefaultMindspace().catch((err) => logError("AppShell: mindspace init failed", err));
    }
  }, [initialDataLoaded, isMindspaceInitialized, isMindspaceLoading, loadDefaultMindspace]);

  // Load chat history when initial data and session are ready
  const sessionId = useChatStore((s) => s.sessionId);
  const fetchAndLoadHistory = useChatStore((s) => s.fetchAndLoadHistory);
  useEffect(() => {
    if (initialDataLoaded && sessionId) {
      logDebug(`AppShell: loading history for session ${sessionId}...`);
      fetchAndLoadHistory();
    }
  }, [initialDataLoaded, sessionId, fetchAndLoadHistory]);

  return (
    <div id="smartie-fullpage-root-shell" className={styles.shell}>
      {/* Mobile header toggle */}
      <div className={styles.mobileHeader}>
        <button
          className={styles.menuButton}
          aria-label="Deschide meniul"
          aria-expanded={isSidebarOpenOnMobile}
          onClick={toggleSidebarMobile}
        >
          ☰
        </button>
        <div className={styles.mobileBrand}>Smartie</div>
      </div>

      {/* Sidebar and overlay */}
      {isSidebarOpenOnMobile && (
        <div
          className={styles.backdrop}
          onClick={closeSidebarMobile}
          aria-hidden="true"
        />
      )}
      <nav
        className={
          isSidebarOpenOnMobile ? `${styles.sidebar} ${styles.sidebarOpen}` : styles.sidebar
        }
        aria-label="Navigare principală"
      >
        <div className={styles.sidebarHeaderMobileOnly}>
          <button
            ref={closeBtnRef}
            className={styles.closeSidebarBtn}
            onClick={closeSidebarMobile}
            aria-label="Închide meniul"
          >
            ✕
          </button>
        </div>
        <AppSidebar />
      </nav>
      <main className={styles.main} role="main">
        {route === "welcome" && <ChatSidebarView />}
        {route === "chat" && <ChatSidebarView />}
        {route === "documents" && <DocumentsPage />}
        {route === "prompts" && <PromptsPage />}
        {route === "mindspace" && <MindspaceInfoPage />}
      </main>
    </div>
  );
};

export default AppShell;


